import {Extension} from "@tiptap/core";
import {PluginKey} from "@tiptap/pm/state";
import {ReactRenderer} from "@tiptap/react";
import Suggestion from "@tiptap/suggestion";
import type {SuggestionKeyDownProps, SuggestionProps} from "@tiptap/suggestion";
import {t} from "i18next";
import type {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import {
    getPrintoutPickerVariables,
    printoutPickerVariableMatches,
    type PrintoutPickerVariable,
} from "./printoutPickerVariables";
import PrintoutVariableMenu, {type PrintoutVariableMenuHandle} from "./PrintoutVariableMenu";

export const PrintoutVariableSlashCommandPluginKey = new PluginKey("printoutVariableSlashCommand");

export interface PrintoutVariableSlashCommandOptions {
    printoutType: PrintoutType | null;
}

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        printoutVariableSlashCommand: {
            setPrintoutVariableCatalog: (options: PrintoutVariableSlashCommandOptions) => ReturnType;
        };
    }
}

// Mutable storage updates the catalogue without rebuilding the editor and losing undo history.
export const PrintoutVariableSlashCommand = Extension.create<
    PrintoutVariableSlashCommandOptions,
    PrintoutVariableSlashCommandOptions
>({
    name: "printoutVariableSlashCommand",

    addOptions() {
        return {
            printoutType: null,
        };
    },

    addStorage() {
        return {
            printoutType: this.options.printoutType,
        };
    },

    addCommands() {
        return {
            setPrintoutVariableCatalog: (options) => () => {
                this.storage.printoutType = options.printoutType;
                return true;
            },
        };
    },

    addProseMirrorPlugins() {
        return [
            Suggestion<PrintoutPickerVariable>({
                // Variable labels are multi-word.
                allowSpaces: true,
                char: "/",
                editor: this.editor,
                pluginKey: PrintoutVariableSlashCommandPluginKey,

                items: ({query}) => getPrintoutPickerVariables(this.storage.printoutType)
                    .filter((item) => printoutPickerVariableMatches(item, query, t)),

                command: ({editor, range, props}) => {
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .insertPrintoutVariable({
                            key: props.key,
                            language: null,
                        })
                        .run();
                },

                render: () => {
                    let component: ReactRenderer<PrintoutVariableMenuHandle> | null = null;

                    const menuProps = (props: SuggestionProps<PrintoutPickerVariable>) => ({
                        items: props.items,
                        query: props.query,
                        command: props.command,
                        clientRect: props.clientRect,
                    });

                    return {
                        onStart: (props: SuggestionProps<PrintoutPickerVariable>) => {
                            component = new ReactRenderer(PrintoutVariableMenu, {
                                editor: props.editor,
                                props: menuProps(props),
                            });
                            document.body.appendChild(component.element);
                        },
                        onUpdate: (props: SuggestionProps<PrintoutPickerVariable>) => {
                            component?.updateProps(menuProps(props));
                        },
                        onKeyDown: (props: SuggestionKeyDownProps) =>
                            component?.ref?.onKeyDown(props.event) ?? false,
                        onExit: () => {
                            component?.element.remove();
                            component?.destroy();
                            component = null;
                        },
                    };
                },
            }),
        ];
    },
});

export default PrintoutVariableSlashCommand;
